import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CommercialApiService, CommercialModuleDefinition, IndustrySolution } from './commercial-api.service';
import { DynamicApiService, StorefrontFormCatalogItem } from '../dynamic/dynamic-api.service';

@Component({
  standalone: true,
  selector: 'ain-marketplace-industry-solutions',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    EditorModule,
    SelectModule,
    MultiSelectModule,
    TextareaModule,
    OfferingSelectorComponent,
    CheckboxModule
  ],
  template: `
    <ain-page [title]="t('commercial.industrySolutions.title')" [description]="t('commercial.industrySolutions.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'commercial.industrySolutions.help.title'"
        [descriptionKey]="'commercial.industrySolutions.help.description'"
        [bulletKeys]="[
          'commercial.industrySolutions.help.bullets.tenant',
          'commercial.industrySolutions.help.bullets.offerings',
          'commercial.industrySolutions.help.bullets.assessments',
          'commercial.industrySolutions.help.bullets.seo'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('commercial.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('commercial.errorLoad')" [message]="error() ?? ''" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('commercial.industrySolutions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('commercial.actions.refresh')" (click)="load()"></button>
            <button
              pButton
              type="button"
              [label]="t('commercial.industrySolutions.edit')"
              [disabled]="!selectedRow()"
              (click)="openEdit()"></button>
          </div>
          @if (info()) {
            <p class="info">{{ info() }}</p>
          }
          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('commercial.industrySolutions.empty')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              selectionMode="single"
              [selection]="selectedRow()"
              (selectionChange)="onSelected($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"></th>
                  <th>{{ t('commercial.industrySolutions.fields.code') }}</th>
                  <th>{{ t('commercial.industrySolutions.fields.title') }}</th>
                  <th>{{ t('commercial.industrySolutions.fields.slug') }}</th>
                  <th>{{ t('commercial.industrySolutions.fields.status') }}</th>
                  <th>{{ t('commercial.industrySolutions.fields.sortOrder') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr [pSelectableRow]="row">
                  <td><p-tableRadioButton [value]="row" /></td>
                  <td>{{ row.code }}</td>
                  <td>{{ row.title }}</td>
                  <td>{{ row.slug }}</td>
                  <td>{{ statusLabel(row.status) }}</td>
                  <td>{{ row.sortOrder }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog [header]="dialogTitle()" [(visible)]="dialogVisible" [modal]="true" [style]="{ width: 'min(960px, 96vw)' }">
        <div class="form-grid">
          @if (!editing()) {
            <label>
              <span>{{ t('commercial.industrySolutions.fields.code') }}</span>
              <input pInputText [(ngModel)]="form.code" />
            </label>
          }
          <label class="full">
            <span>{{ t('commercial.industrySolutions.fields.slug') }}</span>
            <input pInputText [(ngModel)]="form.slug" />
          </label>
          <label class="full">
            <span>{{ t('commercial.industrySolutions.fields.title') }}</span>
            <input pInputText [(ngModel)]="form.title" />
          </label>
          <label class="full">
            <span>{{ t('commercial.industrySolutions.fields.shortDescription') }}</span>
            <input pInputText [(ngModel)]="form.shortDescription" />
          </label>
          <label class="full">
            <span>{{ t('commercial.industrySolutions.fields.seoTitle') }}</span>
            <input pInputText [(ngModel)]="form.seoTitle" />
          </label>
          <label class="full">
            <span>{{ t('commercial.industrySolutions.fields.seoDescription') }}</span>
            <textarea pInputTextarea rows="3" [(ngModel)]="form.seoDescription"></textarea>
          </label>
          <div class="full editor-wrapper">
            <span>{{ t('commercial.industrySolutions.fields.seoContentHtml') }}</span>
            <p-editor [(ngModel)]="form.seoContentHtml" [style]="{ height: '240px' }"></p-editor>
          </div>
          <label class="full">
            <span>{{ t('commercial.industrySolutions.fields.linkedModules') }}</span>
            <p-multiselect
              [options]="moduleOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedModuleKeys"
              [showClear]="true"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('commercial.industrySolutions.fields.recommendedOffering') }}</span>
            <ain-offering-selector [(ngModel)]="form.recommendedOfferingId" [showClear]="true" />
          </label>
          <label>
            <span>{{ t('commercial.industrySolutions.fields.heroOffering') }}</span>
            <ain-offering-selector [(ngModel)]="form.heroOfferingId" [showClear]="true" />
          </label>
          <label class="full">
            <span>{{ t('commercial.industrySolutions.fields.relatedAssessments') }}</span>
            <p-multiselect
              [options]="assessmentOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedAssessmentIds"
              [showClear]="true"
              appendTo="body" />
          </label>
          <label class="full checkbox-row">
            <p-checkbox [(ngModel)]="form.useOfferingAssessmentFallback" [binary]="true" inputId="useOfferingAssessmentFallback" />
            <label for="useOfferingAssessmentFallback">{{ t('commercial.industrySolutions.fields.useOfferingAssessmentFallback') }}</label>
          </label>
          <label>
            <span>{{ t('commercial.industrySolutions.fields.status') }}</span>
            <p-select
              [options]="statusOptions"
              optionValue="value"
              [(ngModel)]="form.status"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? statusLabel(item.value ?? item) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ statusLabel(item.value) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('commercial.industrySolutions.fields.sortOrder') }}</span>
            <input pInputText type="number" [(ngModel)]="form.sortOrder" />
          </label>
        </div>
        @if (dialogError()) {
          <p class="error">{{ dialogError() }}</p>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid var(--ain-color-border, #e5e7eb); border-radius: 14px; padding: 16px; background: var(--ain-color-surface); }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .form-grid label { display: flex; flex-direction: column; gap: 6px; }
    .form-grid label.full, .form-grid .full { grid-column: 1 / -1; }
    .editor-wrapper { display: grid; gap: 6px; }
    .checkbox-row { flex-direction: row !important; align-items: center; gap: 8px; }
    .info { margin: 0 0 12px; color: var(--ain-color-text-muted, #64748b); }
    .error { color: #b91c1c; margin-top: 8px; }
  `]
})
export class MarketplaceIndustrySolutionsComponent implements OnInit {
  private readonly api = inject(CommercialApiService);
  private readonly dynamicApi = inject(DynamicApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly rows = signal<IndustrySolution[]>([]);
  readonly modules = signal<CommercialModuleDefinition[]>([]);
  readonly moduleOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly assessmentOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly selectedRow = signal<IndustrySolution | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly saving = signal(false);
  readonly dialogError = signal<string | null>(null);
  readonly editing = signal(false);
  readonly dialogTitle = signal('');

  dialogVisible = false;
  selectedModuleKeys: string[] = [];
  selectedAssessmentIds: string[] = [];
  form = {
    code: '',
    slug: '',
    title: '',
    shortDescription: '',
    seoTitle: '',
    seoDescription: '',
    seoContentHtml: '',
    recommendedOfferingId: null as string | null,
    heroOfferingId: null as string | null,
    status: 'Draft',
    sortOrder: 0,
    useOfferingAssessmentFallback: true
  };

  readonly statusOptions = [
    { value: 'Draft', labelKey: 'commercial.industrySolutions.status.draft' },
    { value: 'Active', labelKey: 'commercial.industrySolutions.status.active' },
    { value: 'Archived', labelKey: 'commercial.industrySolutions.status.archived' }
  ];

  ngOnInit(): void {
    this.load();
    this.api.listModules().subscribe({
      next: items => {
        this.modules.set(items);
        this.moduleOptions.set(items.map(item => ({ label: `${item.name} (${item.moduleKey})`, value: item.moduleKey })));
      }
    });
    this.dynamicApi.listStorefrontCatalogItems().subscribe({
      next: (items: StorefrontFormCatalogItem[]) => {
        this.assessmentOptions.set(
          items.map(item => ({
            label: `${item.displayTitle} (${item.publicationSlug})`,
            value: item.id
          }))
        );
      }
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listIndustrySolutions().subscribe({
      next: items => {
        this.rows.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('commercial.industrySolutions.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  onSelected(row: IndustrySolution | IndustrySolution[] | null): void {
    const selected = Array.isArray(row) ? row[0] ?? null : row;
    this.selectedRow.set(selected);
  }

  openCreate(): void {
    this.editing.set(false);
    this.dialogTitle.set(this.t('commercial.industrySolutions.create'));
    this.form = {
      code: '',
      slug: '',
      title: '',
      shortDescription: '',
      seoTitle: '',
      seoDescription: '',
      seoContentHtml: '',
      recommendedOfferingId: null,
      heroOfferingId: null,
      status: 'Draft',
      sortOrder: this.rows().length + 1,
      useOfferingAssessmentFallback: true
    };
    this.selectedModuleKeys = [];
    this.selectedAssessmentIds = [];
    this.dialogError.set(null);
    this.dialogVisible = true;
  }

  openEdit(): void {
    const row = this.selectedRow();
    if (!row) {
      return;
    }

    this.editing.set(true);
    this.dialogTitle.set(`${this.t('commercial.industrySolutions.edit')}: ${row.title}`);
    this.form = {
      code: row.code,
      slug: row.slug,
      title: row.title,
      shortDescription: row.shortDescription,
      seoTitle: row.seoTitle,
      seoDescription: row.seoDescription,
      seoContentHtml: row.seoContentHtml,
      recommendedOfferingId: row.recommendedOfferingId,
      heroOfferingId: row.heroOfferingId,
      status: row.status,
      sortOrder: row.sortOrder,
      useOfferingAssessmentFallback: row.useOfferingAssessmentFallback
    };
    this.selectedModuleKeys = this.parseModuleKeys(row.linkedModuleKeysJson);
    this.selectedAssessmentIds = [...(row.assessmentLinks ?? [])]
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map(link => link.catalogItemId);
    this.dialogError.set(null);
    this.dialogVisible = true;
  }

  save(): void {
    if (!this.form.slug.trim() || !this.form.title.trim()) {
      this.dialogError.set(this.t('commercial.validation.required'));
      return;
    }

    if (!this.editing() && !this.form.code.trim()) {
      this.dialogError.set(this.t('commercial.validation.required'));
      return;
    }

    const linkedModuleKeysJson = JSON.stringify(this.selectedModuleKeys);
    const assessmentLinks = this.selectedAssessmentIds.map((catalogItemId, index) => ({
      catalogItemId,
      sortOrder: index
    }));
    this.saving.set(true);
    this.dialogError.set(null);

    const request$ = this.editing()
      ? this.api.updateIndustrySolution(this.selectedRow()!.id, {
          slug: this.form.slug.trim(),
          title: this.form.title.trim(),
          shortDescription: this.form.shortDescription.trim(),
          seoTitle: this.form.seoTitle.trim(),
          seoDescription: this.form.seoDescription.trim(),
          seoContentHtml: this.form.seoContentHtml.trim(),
          linkedModuleKeysJson,
          recommendedOfferingId: this.form.recommendedOfferingId,
          heroOfferingId: this.form.heroOfferingId,
          status: this.form.status,
          sortOrder: Number(this.form.sortOrder) || 0,
          useOfferingAssessmentFallback: this.form.useOfferingAssessmentFallback,
          assessmentLinks
        })
      : this.api.createIndustrySolution({
          code: this.form.code.trim(),
          slug: this.form.slug.trim(),
          title: this.form.title.trim(),
          shortDescription: this.form.shortDescription.trim(),
          seoTitle: this.form.seoTitle.trim(),
          seoDescription: this.form.seoDescription.trim(),
          seoContentHtml: this.form.seoContentHtml.trim(),
          linkedModuleKeysJson,
          recommendedOfferingId: this.form.recommendedOfferingId,
          heroOfferingId: this.form.heroOfferingId,
          status: this.form.status,
          sortOrder: Number(this.form.sortOrder) || 0,
          useOfferingAssessmentFallback: this.form.useOfferingAssessmentFallback,
          assessmentLinks
        });

    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogVisible = false;
        this.info.set(this.editing() ? this.t('commercial.industrySolutions.updated') : this.t('commercial.industrySolutions.created'));
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving.set(false);
        this.dialogError.set(this.resolveError(err, 'commercial.industrySolutions.errorSave'));
      }
    });
  }

  statusLabel(status: string): string {
    const match = this.statusOptions.find(item => item.value === status);
    return match ? this.t(match.labelKey) : status;
  }

  private parseModuleKeys(json: string): string[] {
    try {
      const parsed = JSON.parse(json || '[]');
      return Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  private resolveError(error: HttpErrorResponse, fallbackKey: string): string {
    if (error.status === 409) {
      return this.t('commercial.errors.conflict');
    }
    if (error.status === 400) {
      return this.t('commercial.errors.invalid');
    }
    return this.t(fallbackKey);
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
